/**
 * useSpeechPlayback — browser-native text-to-speech for FutureBot replies.
 *
 * Wraps window.speechSynthesis. Replies are markdown, so they're flattened
 * with stripMarkdownForSpeech first, then split into sentence-sized chunks —
 * Chrome silently cuts off long utterances (~15s), and short queued utterances
 * sidestep that entirely. Consecutive speak() calls queue rather than
 * interrupt (a tool-round interstitial reply shouldn't be cut off by the
 * final answer); stop() clears everything.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_CHUNK_CHARS = 200;

/** Flatten markdown into speakable plain text. */
export function stripMarkdownForSpeech(markdown) {
  if (!markdown) return '';
  let text = String(markdown);

  // Block content that reads terribly out loud.
  text = text.replace(/```[\s\S]*?```/g, ' Code block omitted. ');
  // GFM tables: 2+ consecutive lines containing pipes.
  text = text.replace(/(?:^[^\n]*\|[^\n]*\n?){2,}/gm, ' Table omitted. ');

  // Inline constructs -> their readable content.
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1'); // images -> alt text
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1'); // links -> label
  text = text.replace(/`([^`]+)`/g, '$1'); // inline code
  text = text.replace(/(\*\*|__)(.*?)\1/g, '$2'); // bold
  text = text.replace(/(\*|_)([^*_\n]+)\1/g, '$2'); // italics

  // Line-level markers.
  text = text.replace(/^#{1,6}\s+/gm, '');
  text = text.replace(/^>\s?/gm, '');
  text = text.replace(/^\s*[-*+]\s+/gm, '');
  text = text.replace(/^\s*\d+\.\s+/gm, '');
  text = text.replace(/^ *([-*_] *){3,}$/gm, ' ');

  return text.replace(/\s+/g, ' ').trim();
}

/** Split text into utterance-sized chunks on sentence, then word, boundaries. */
function chunkForSpeech(text, maxLen = MAX_CHUNK_CHARS) {
  if (!text) return [];
  // Sentence split without lookbehind (older-Safari-safe).
  const sentences = text
    .replace(/([.!?])\s+/g, '$1\u0000')
    .split('\u0000')
    .flatMap((sentence) => {
      const s = sentence.trim();
      if (!s) return [];
      if (s.length <= maxLen) return [s];
      const pieces = [];
      let piece = '';
      for (const word of s.split(/\s+/)) {
        if (piece && piece.length + 1 + word.length > maxLen) {
          pieces.push(piece);
          piece = word;
        } else {
          piece = piece ? `${piece} ${word}` : word;
        }
      }
      if (piece) pieces.push(piece);
      return pieces;
    });

  // Merge short sentences back together up to the cap.
  const chunks = [];
  let current = '';
  for (const s of sentences) {
    if (current && current.length + 1 + s.length > maxLen) {
      chunks.push(current);
      current = s;
    } else {
      current = current ? `${current} ${s}` : s;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

function pickVoice() {
  const voices = window.speechSynthesis.getVoices() || [];
  return (
    voices.find((v) => v.name === 'Google US English') ||
    voices.find((v) => v.lang === 'en-US' && v.localService) ||
    voices.find((v) => v.lang === 'en-US') ||
    null
  );
}

export function useSpeechPlayback() {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const remainingRef = useRef(0); // utterances outstanding across queued speak() calls
  const epochRef = useRef(0); // bumped by stop(); invalidates in-flight callbacks

  useEffect(() => {
    if (!supported) return undefined;
    // Chrome loads the voice list lazily — warm it so the first reply doesn't
    // fall back to the default voice.
    window.speechSynthesis.getVoices();
    return () => window.speechSynthesis.cancel();
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    epochRef.current += 1;
    remainingRef.current = 0;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [supported]);

  const speak = useCallback(
    (markdown) => {
      if (!supported) return;
      const chunks = chunkForSpeech(stripMarkdownForSpeech(markdown));
      if (chunks.length === 0) return;

      const epoch = epochRef.current;
      const voice = pickVoice();
      remainingRef.current += chunks.length;
      setIsSpeaking(true);

      const handleDone = () => {
        if (epoch !== epochRef.current) return; // superseded by stop()
        remainingRef.current = Math.max(0, remainingRef.current - 1);
        if (remainingRef.current === 0) setIsSpeaking(false);
      };

      for (const chunk of chunks) {
        const utterance = new SpeechSynthesisUtterance(chunk);
        if (voice) utterance.voice = voice;
        utterance.rate = 1.0;
        utterance.onend = handleDone;
        utterance.onerror = handleDone;
        window.speechSynthesis.speak(utterance);
      }
    },
    [supported]
  );

  return { supported, isSpeaking, speak, stop };
}

export default useSpeechPlayback;
